import { Container, FillGradient, Graphics, Text } from 'pixi.js';
import { ReelContainer } from './ReelContainer.js';
import { UILayer } from './UILayer.js';
import { WinCelebrationOverlay } from './WinCelebrationOverlay.js';
import { WinFloater } from './WinFloater.js';
import { gameConfig } from '../config/gameConfig.js';

export class SlotMachine {
  /**
   * @param {{
   *   spineAssets: Record<number, any>,
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   *   onSpinRequest: () => void,
   *   translate?: (key: string, replace?: Record<string, string>) => string,
   *   onBetLayoutChanged?: () => void,
   *   onBetSync?: (value: number) => void,
   *   onLinesSync?: (value: number) => void,
   *   onBetPopupToggle?: () => void,
   * }} opts
   */
  constructor(opts) {
    this.animationController = opts.animationController;
    this.onSpinRequest = opts.onSpinRequest;
    this.theme = gameConfig.theme;
    this.u = gameConfig.ui;
    this._translate = typeof opts.translate === 'function' ? opts.translate : null;

    this.root = new Container();

    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;
    const pad = gameConfig.machinePadding;
    this._columnW = gameConfig.columnWidth;
    this._reelLeft = pad + this._columnW;
    this._frameOuterW = reelBlockW + pad * 2 + this._columnW * 2;
    this._frameOuterH = reelBlockH + pad * 2;
    const headerH = gameConfig.headerHeight;

    /** Logical width for full-bleed header + bottom bar (≥ cabinet width). */
    this._layoutBleedW = this._frameOuterW;

    this._headerLayer = new Container();
    this.root.addChild(this._headerLayer);

    /** Seamless dark backdrop that fills the gap between header and control panel on mobile. */
    this._midBackdrop = new Graphics();
    this._midBackdrop.eventMode = 'none';
    this._midBackdrop.visible = false;
    this.root.addChild(this._midBackdrop);

    this._cabinetGroup = new Container();
    this.root.addChild(this._cabinetGroup);

    this._drawHeader(headerH);
    this._drawCabinetFrame(reelBlockW, reelBlockH, pad, headerH);
    this._drawMarbleColumns(headerH);
    this._drawReelWell(reelBlockW, reelBlockH, pad, headerH);
    this._drawPediment(reelBlockW, reelBlockH, pad, headerH);

    /** Reel chrome (grid / column separators) behind symbols so animated tiles paint on top. */
    this._drawReelChrome(reelBlockW, reelBlockH, pad, headerH);

    this.reelContainer = new ReelContainer({
      spineAssets: opts.spineAssets,
      animationController: this.animationController,
    });
    this.reelContainer.root.x = this._reelLeft;
    this.reelContainer.root.y = headerH + pad;
    this._cabinetGroup.addChild(this.reelContainer.root);

    /** Full-viewport win popup; parented on {@link #root} so it stays on screen and paints above the cabinet. */
    this.winCelebration = new WinCelebrationOverlay(opts.spineAssets);
    this.root.sortableChildren = true;
    this.winCelebration.root.zIndex = 99999;
    this.root.addChild(this.winCelebration.root);

    const barTopY = headerH + this._frameOuterH + this.u.gapCabinetToBar;
    this.winFloater = new WinFloater(this.animationController, {
      width: this._layoutBleedW,
      barTopY,
    });
    this.root.addChild(this.winFloater.root);

    this.ui = new UILayer({
      animationController: this.animationController,
      onSpin: () => this.onSpinRequest(),
      translate: opts.translate,
      panelWidth: this._layoutBleedW,
      panelHeight: gameConfig.controlBarHeight,
      onBetLayoutChanged: opts.onBetLayoutChanged,
      onBetSync: opts.onBetSync,
      onLinesSync: opts.onLinesSync,
      onAutoplayToggle: opts.onAutoplayToggle,
      onMaxBet: opts.onMaxBet,
      onSettingsToggle: opts.onSettingsToggle,
      onBetPopupToggle: opts.onBetPopupToggle,
    });
    this.ui.root.x = 0;
    this.ui.root.y = barTopY;
    this.root.addChild(this.ui.root);
  }

  /**
   * Stretch header + bottom bar to viewport logical width; cabinet stays centered.
   * @param {number} logicalWidth renderer pixels / current world scale
   */
  setLayoutBleedWidth(logicalWidth) {
    const nw = Math.max(this._frameOuterW, Math.ceil(logicalWidth));
    if (nw === Math.round(this._layoutBleedW)) return;
    this._layoutBleedW = nw;
    this._cabinetGroup.x = (nw - this._frameOuterW) / 2;
    this._redrawHeaderBleed();
    this.ui.setPanelWidth(nw);
    this.winFloater.setLayoutWidth(nw);
  }

  setViewportProfile(profile) {
    this.ui.setLayoutMode(profile);
  }

  /** Refresh header copy when the translation function updates (e.g. API culture load). */
  refreshLocalizedHeaderTexts() {
    const t = this._translate;
    if (!t || !this._headerTitle || !this._headerSub) return;
    this._headerTitle.text = t('GAME_TITLE');
    this._headerSub.text = t('HEADER_SUBTITLE');
  }

  /**
   * Decoupled screen-space layout.
   *
   * Pins the header to the top edge, the control panel to the bottom edge, and fits the
   * reel cabinet in the middle. Each section has its own scale, so the cabinet can shrink
   * on narrow phones without making the header/UI chrome unreadable.
   *
   * Assumes the parent world is at identity scale; caller places slotMachine.root origin
   * at the top-left of the screen.
   *
   * @param {number} screenW screen pixels (viewport width)
   * @param {number} screenH screen pixels (viewport height)
   * @param {string} profile e.g. 'mobile-portrait'
   */
  layoutScreen(screenW, screenH, profile) {
    const headerH = gameConfig.headerHeight;
    const isMobile = profile.startsWith('mobile');
    const isTablet = profile.startsWith('tablet');
    const isMobileLandscape = profile === 'mobile-landscape';
    const chromeScale = isMobile
      ? (isMobileLandscape ? 0.92 : 0.98)
      : isTablet
        ? 1.15
        : 1.3;

    const uiNatW = Math.max(320, Math.round(screenW / chromeScale));
    const headerNatW = uiNatW;

    if (Math.round(this._layoutBleedW) !== headerNatW) {
      this._layoutBleedW = headerNatW;
      this._redrawHeaderBleed();
    }

    this.ui.setPanelWidth(uiNatW, true);
    this.ui.root.scale.set(chromeScale);
    const panelScreenH = this.ui.panelHeight * chromeScale;
    this.ui.root.x = 0;
    this.ui.root.y = screenH - panelScreenH;

    this._headerLayer.scale.set(chromeScale);
    this._headerLayer.x = 0;
    this._headerLayer.y = 0;
    const headerScreenH = headerH * chromeScale;

    const midTop = headerScreenH;
    const midBottom = screenH - panelScreenH;
    const midH = Math.max(60, midBottom - midTop);

    /** On mobile we hide decorative chrome and let the reel block dominate the screen width. */
    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth +
      (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const sideGap = isMobile ? 4 : 0;

    if (!this._hideDecor) {
      if (this._cabinetFrameG) this._cabinetFrameG.visible = !isMobile;
      if (this._marbleLeft) this._marbleLeft.visible = !isMobile;
      if (this._marbleRight) this._marbleRight.visible = !isMobile;
      if (this._pedimentG) this._pedimentG.visible = !isMobile;
      this._midBackdrop.visible = isMobile;
    }

    let cabinetScale;
    if (isMobile) {
      /** Scale so the 5-reel block sits sideGap from each edge. Decorations clip off-screen (hidden). */
      cabinetScale = (screenW - sideGap * 2) / reelBlockW;
      const maxByHeight = midH / this._frameOuterH;
      if (cabinetScale > maxByHeight) cabinetScale = maxByHeight;
    } else {
      cabinetScale = Math.min(
        screenW / this._frameOuterW,
        midH / this._frameOuterH,
      );
    }

    const cabMul = Math.max(0.25, Math.min(1.5, this.u.slotCabinetScaleMul ?? 1));
    cabinetScale *= cabMul;

    this._cabinetGroup.scale.set(cabinetScale);
    const cabScreenH = this._frameOuterH * cabinetScale;

    if (isMobile) {
      /** Reel block starts at local x=_reelLeft. Align its left edge to `sideGap` on screen. */
      this._cabinetGroup.x = sideGap - this._reelLeft * cabinetScale;
    } else {
      const cabScreenW = this._frameOuterW * cabinetScale;
      this._cabinetGroup.x = (screenW - cabScreenW) / 2;
    }

    /** Cabinet draws from local y=headerH downward. Put reels vertically centered in middle zone. */
    const cabTopScreen = midTop + (midH - cabScreenH) / 2;
    this._cabinetGroup.y = cabTopScreen - headerH * cabinetScale;

    if (isMobile) {
      this._paintMidBackdrop(screenW, midTop, midH, cabTopScreen, cabScreenH);
    }

    const winFloaterOffsetY = isMobile ? 26 : 4;
    this.winFloater.setLayoutWidth(uiNatW);
    this.winFloater.root.scale.set(chromeScale);
    this.winFloater.root.x = screenW / 2;
    this.winFloater.root.y = screenH - panelScreenH - winFloaterOffsetY;

    this.winCelebration.setScreenCenterLayout(screenW, screenH);
  }

  /**
   * Draws the seamless middle backdrop behind the reels so the gap between header
   * and control panel reads as a single "cabinet interior" rather than empty space.
   * Adds a subtle inner glow ring around the reel area so reels feel inset, not floating.
   */
  _paintMidBackdrop(screenW, midTop, midH, cabTopScreen, cabScreenH) {
    const g = this._midBackdrop;
    g.clear();

    const fullGrad = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: 0x10081c },
        { offset: 0.42, color: this.theme.reelWell },
        { offset: 1, color: 0x08050f },
      ],
    });
    g.rect(0, midTop, screenW, midH).fill(fullGrad);

    /* Header rule */
    g.moveTo(0, midTop).lineTo(screenW, midTop).stroke({
      width: 1,
      color: this.theme.gold,
      alpha: 0.65,
    });
    /* Bottom rule */
    g.moveTo(0, midTop + midH).lineTo(screenW, midTop + midH).stroke({
      width: 1,
      color: this.theme.gold,
      alpha: 0.65,
    });

    /* Reel inset glow ring */
    const reelPadY = 10;
    const reelPadX = 4;
    const glowX = reelPadX;
    const glowW = screenW - reelPadX * 2;
    const glowY = cabTopScreen - reelPadY;
    const glowH = cabScreenH + reelPadY * 2;
    const rr = 14;
    g.roundRect(glowX, glowY, glowW, glowH, rr).stroke({
      width: 1.5,
      color: this.theme.gold,
      alpha: 0.5,
    });
    g.roundRect(glowX + 2, glowY + 2, glowW - 4, glowH - 4, rr - 2).stroke({
      width: 1,
      color: 0xffffff,
      alpha: 0.07,
    });
  }

  _redrawHeaderBleed() {
    const headerH = gameConfig.headerHeight;
    const w = this._layoutBleedW;
    this._headerBg.clear();
    const grad = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: this.theme.headerShine },
        { offset: 0.45, color: this.theme.panelMid },
        { offset: 1, color: this.theme.panelDeep },
      ],
    });
    this._headerBg.roundRect(0, 0, w, headerH, 0).fill(grad);
    this._headerBg.moveTo(0, headerH - 1).lineTo(w, headerH - 1).stroke({
      width: this.u.strokeHeaderRule,
      color: this.theme.gold,
      alpha: 0.88,
    });
    this._headerBg.moveTo(0, 1).lineTo(w, 1).stroke({ width: 1, color: 0xffffff, alpha: 0.06 });
    const hh = headerH;
    this._paintHeaderBolt(this._headerBoltL, w * 0.2, hh);
    this._paintHeaderBolt(this._headerBoltR, w * 0.8, hh);
    /* Slightly left-biased to not crowd the top-right settings button on narrow screens */
    this._headerTitle.x = Math.round(w * 0.48);
    this._headerSub.x = Math.round(w * 0.48);
  }

  /**
   * Reset all round visuals before a new spin (floater, reels, paylines).
   * Note: the win celebration overlay is closed separately by the caller via
   * `skipWinCelebration()` so the tier `endXxxWin` Spine clip always plays to completion
   * instead of being hard-hidden here.
   */
  prepareForNewRound() {
    this.winFloater?.reset();
    this.reelContainer.prepareForNewRound();
  }

  /** @deprecated Use {@link #prepareForNewRound} */
  clearWinRollup() {
    this.prepareForNewRound();
  }

  /**
   * Win celebration Spine overlay from 5× stake (5–10× big, 10–20× mega, 20×+ epic).
   * @param {number} multiple win ÷ stake
   * @param {boolean} turbo fast tier (turbo snap / tier 3)
   */
  /**
   * @param {number} multiple win ÷ stake
   * @param {boolean} turbo
   * @param {number} totalWinAmount round win (count-up synced to Spine build-up duration)
   */
  playWinCelebration(multiple, turbo, totalWinAmount, onBuildupComplete) {
    this.winCelebration?.play(multiple, turbo, totalWinAmount, undefined, onBuildupComplete);
  }

  /** Post-buildup hold pose for session resume (no animation replay). */
  showWinCelebrationStaticHold(multiple, totalWinAmount) {
    this.winCelebration?.showStaticHold(multiple, totalWinAmount);
  }

  /** Resume celebration from elapsed time after session interrupt. */
  playWinCelebrationContinue(multiple, totalWinAmount, elapsedSec, onBuildupComplete) {
    this.winCelebration?.playContinue(multiple, totalWinAmount, elapsedSec, onBuildupComplete);
  }

  /** Collect tap: play tier end clip (`endHugeWin`, etc.) then hide. @returns {Promise<void>} */
  skipWinCelebration() {
    return this.winCelebration?.skip() ?? Promise.resolve();
  }

  /**
   * Cycles winning paylines on the reels and shows the round total in the win popup.
   * @param {{ paylineIndex: number, symbolId: number, count: number, payout: number }[]} hitsSorted by `paylineIndex`
   * @param {number} totalWinAmount full win for the round
   * @param {(hit: { paylineIndex: number, symbolId: number, count: number, payout: number }) => void} [onPhaseSymbol]
   *   Optional per-phase callback fired with the current winning hit (used for symbol-specific SFX).
   */
  beginPostSpinWin(hitsSorted, totalWinAmount, onPhaseSymbol) {
    if (!hitsSorted.length) return;
    this.reelContainer.startWinLineCycle(hitsSorted, totalWinAmount, (amt, hit) => {
      this.winFloater.setWinAmount(amt);
      if (typeof onPhaseSymbol === 'function' && hit) {
        try { onPhaseSymbol(hit); } catch (_e) { /* never let SFX break the cycle */ }
      }
    });
  }

  _lightningBolt(g, x, y, scale) {
    const s = scale;
    g.moveTo(x, y - 18 * s);
    g.lineTo(x + 6 * s, y - 2 * s);
    g.lineTo(x - 2 * s, y + 2 * s);
    g.lineTo(x + 10 * s, y + 22 * s);
    g.lineTo(x + 2 * s, y + 4 * s);
    g.lineTo(x + 14 * s, y);
    g.closePath();
  }

  _paintHeaderBolt(g, bx, headerH) {
    g.clear();
    this._lightningBolt(g, bx, headerH / 2 + 2, 1.05);
    g.fill({ color: this.theme.lightning, alpha: 0.92 });
    g.stroke({ width: 1, color: this.theme.goldBright, alpha: 0.5 });
  }

  _drawHeader(headerH) {
    this._headerBg = new Graphics();
    this._headerBg.eventMode = 'none';
    this._headerLayer.addChild(this._headerBg);

    const w = this._layoutBleedW;
    this._headerBoltL = new Graphics();
    this._headerBoltL.eventMode = 'none';
    this._paintHeaderBolt(this._headerBoltL, w * 0.19, headerH);
    this._headerLayer.addChild(this._headerBoltL);

    this._headerBoltR = new Graphics();
    this._headerBoltR.eventMode = 'none';
    this._paintHeaderBolt(this._headerBoltR, w * 0.81, headerH);
    this._headerLayer.addChild(this._headerBoltR);

    this._headerTitle = new Text({
      text: this._translate ? this._translate('GAME_TITLE') : 'Fruity Jungle',
      style: {
        fontFamily: this.theme.fontDisplay,
        fontSize: this.u.typeHeaderTitle,
        fontWeight: '700',
        fill: this.theme.goldBright,
        letterSpacing: 5,
        dropShadow: {
          alpha: 0.9,
          angle: 1.1,
          blur: 16,
          color: this.theme.skyGlow,
          distance: 0,
        },
      },
    });
    this._headerTitle.anchor.set(0.5, 0.5);
    this._headerTitle.x = Math.round(w * 0.48);
    this._headerTitle.y = headerH * 0.45;
    this._headerTitle.eventMode = 'none';
    this._headerLayer.addChild(this._headerTitle);

    this._headerSub = new Text({
      text: this._translate ? this._translate('HEADER_SUBTITLE') : 'CHAIN WINS · HIGH-VOL ACTION',
      style: {
        fontFamily: this.theme.fontUi,
        fontSize: this.u.typeHeaderSub,
        fontWeight: '600',
        fill: this.theme.textMuted,
        letterSpacing: 2.2,
      },
    });
    this._headerSub.anchor.set(0.5, 0);
    this._headerSub.x = Math.round(w * 0.48);
    this._headerSub.y = headerH * 0.66;
    this._headerSub.eventMode = 'none';
    this._headerLayer.addChild(this._headerSub);

    this._redrawHeaderBleed();
  }

  _drawCabinetFrame(reelBlockW, reelBlockH, pad, headerH) {
    const w = this._frameOuterW;
    const h = this._frameOuterH;
    const g = new Graphics();
    this._cabinetFrameG = g;
    const rc = this.u.radiusCabinet;

    const outerGrad = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: 0x3d2560 },
        { offset: 0.35, color: gameConfig.machineFrameColor },
        { offset: 1, color: 0x0a0512 },
      ],
    });
    g.roundRect(0, headerH, w, h, rc).fill(outerGrad);

    g.roundRect(0, headerH, w, h, rc).stroke({
      width: this.u.strokeCabinetOuter,
      color: this.theme.goldDim,
      alpha: 0.96,
    });
    g.roundRect(2, headerH + 2, w - 4, h - 4, Math.max(0, rc - 2)).stroke({
      width: this.u.strokeCabinetMid,
      color: this.theme.goldBright,
      alpha: 0.42,
    });
    g.roundRect(6, headerH + 6, w - 12, h - 12, Math.max(0, rc - 6)).stroke({
      width: this.u.strokeCabinetInner,
      color: 0x000000,
      alpha: 0.45,
    });
    g.eventMode = 'none';
    this._cabinetGroup.addChild(g);
  }

  _drawMarbleColumns(headerH) {
    const pad = gameConfig.machinePadding;
    const w = this._frameOuterW;
    const h = this._frameOuterH;
    const cw = this._columnW;
    const top = headerH + pad;
    const bot = headerH + h - pad;
    const colH = bot - top;
    const inset = 8;
    const rcol = this.u.radiusColumn;

    const left = new Graphics();
    const mGrad = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 1, y: 0 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: this.theme.marbleShadow },
        { offset: 0.45, color: this.theme.marble },
        { offset: 1, color: this.theme.marbleShadow },
      ],
    });
    left.roundRect(inset, top, cw - inset * 2, colH, rcol).fill(mGrad);
    left.roundRect(inset, top, cw - inset * 2, colH, rcol).stroke({
      width: 2,
      color: this.theme.goldDim,
      alpha: 0.75,
    });
    left.roundRect(inset + 1, top + 1, cw - inset * 2 - 2, colH - 2, Math.max(0, rcol - 1)).stroke({
      width: 1,
      color: 0xffffff,
      alpha: 0.12,
    });
    left.eventMode = 'none';
    this._marbleLeft = left;
    this._cabinetGroup.addChild(left);

    const right = new Graphics();
    const mGradR = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 1, y: 0 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: this.theme.marbleShadow },
        { offset: 0.55, color: this.theme.marble },
        { offset: 1, color: this.theme.marbleShadow },
      ],
    });
    const rx = w - cw + inset;
    right.roundRect(rx, top, cw - inset * 2, colH, rcol).fill(mGradR);
    right.roundRect(rx, top, cw - inset * 2, colH, rcol).stroke({
      width: 2,
      color: this.theme.goldDim,
      alpha: 0.75,
    });
    right.roundRect(rx + 1, top + 1, cw - inset * 2 - 2, colH - 2, Math.max(0, rcol - 1)).stroke({
      width: 1,
      color: 0xffffff,
      alpha: 0.12,
    });
    right.eventMode = 'none';
    this._marbleRight = right;
    this._cabinetGroup.addChild(right);
  }

  _drawPediment(reelBlockW, reelBlockH, pad, headerH) {
    const g = new Graphics();
    this._pedimentG = g;
    const ins = this.u.wellInset;
    const x0 = this._reelLeft - ins;
    const y0 = headerH + pad - ins;
    const pw = reelBlockW + ins * 2;
    const peakY = y0 - 20;
    const mid = x0 + pw / 2;
    g.moveTo(x0, y0);
    g.lineTo(mid, peakY);
    g.lineTo(x0 + pw, y0);
    g.closePath();
    g.fill({ color: this.theme.panelMid, alpha: 0.88 });
    g.stroke({ width: 2, color: this.theme.gold, alpha: 0.78 });
    g.eventMode = 'none';
    this._cabinetGroup.addChild(g);
  }

  _drawReelWell(reelBlockW, reelBlockH, pad, headerH) {
    const g = new Graphics();
    this._reelWellG = g;
    const ins = this.u.wellInset;
    const x = this._reelLeft - ins;
    const y = headerH + pad - ins;
    const rw = reelBlockW + ins * 2;
    const rh = reelBlockH + ins * 2;
    const rr = this.u.radiusWell;

    const wellGrad = new FillGradient({
      type: 'radial',
      center: { x: 0.5, y: 0.45 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.65,
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: 0x1a0f28 },
        { offset: 1, color: this.theme.reelWell },
      ],
    });
    g.roundRect(x, y, rw, rh, rr).fill(wellGrad);
    g.roundRect(x, y, rw, rh, rr).stroke({
      width: this.u.strokeWell,
      color: this.theme.reelInnerLine,
      alpha: 1,
    });
    g.roundRect(x + 2, y + 2, rw - 4, rh - 4, Math.max(0, rr - 2)).stroke({
      width: this.u.strokeWellInner,
      color: this.theme.goldDim,
      alpha: 0.3,
    });
    g.eventMode = 'none';
    this._cabinetGroup.addChild(g);
  }

  _drawReelChrome(reelBlockW, reelBlockH, pad, headerH) {
    const g = new Graphics();
    this._reelChromeG = g;
    const step = gameConfig.cellWidth + gameConfig.reelGap;
    const gap = gameConfig.reelGap;
    const x0 = this._reelLeft;
    const y0 = headerH + pad;
    const inset = 8;
    for (let k = 1; k < gameConfig.reelCount; k++) {
      const x = x0 + k * step - gap / 2;
      g.moveTo(x, y0 + inset).lineTo(x, y0 + reelBlockH - inset);
    }
    g.stroke({ width: 2, color: this.theme.goldDim, alpha: 0.6 });
    g.moveTo(x0 + inset, y0 + gameConfig.cellHeight).lineTo(
      x0 + reelBlockW - inset,
      y0 + gameConfig.cellHeight,
    );
    g.moveTo(x0 + inset, y0 + gameConfig.cellHeight * 2).lineTo(
      x0 + reelBlockW - inset,
      y0 + gameConfig.cellHeight * 2,
    );
    g.stroke({ width: 1, color: this.theme.reelInnerLine, alpha: 0.95 });
    g.eventMode = 'none';
    this._cabinetGroup.addChild(g);
  }

  getSize() {
    const headerH = gameConfig.headerHeight;
    const controlH = this.ui.panelHeight;
    const u = gameConfig.ui;
    return {
      width: this._layoutBleedW,
      height: headerH + this._frameOuterH + u.gapCabinetToBar + controlH + u.gapBarBottom,
    };
  }

  getCabinetWidth() {
    return this._frameOuterW;
  }
}
